import { useCallback } from 'react'
import { useCreateTopic } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { PrivacyType, TopicType } from '@/utils/constants'
import { withSourceAlbumIds } from '@/features/gallery-studio/utils/galleryXData'

/**
 * Lean "stub" GALLERY create — no photo-count gate, no wizard call. Lands
 * on the real `/galleries/:galleryId/studio` id immediately.
 *
 * Without a `sourceAlbumId` ("New gallery"), the stub carries no album link
 * at all; `useStudio3DData` reads that as `isNewGallery` and
 * `StudioFirstRunOverlay` asks "which album" live, inside the canvas,
 * instead of gating navigation on a pre-flight modal.
 *
 * With a `sourceAlbumId` (Album's "Turn into 3D"), the album is already
 * known — skip asking and seed it straight into the gallery's topicXData.
 * `useStudio3DData` then sees a real sourceAlbumId with no saved scene and
 * auto-lays the album's photos into a Classic room (task #17 — Classic is
 * the launch default for every brand-new seed) sized to fit via
 * `layoutClassicArts`, so the room opens populated instead of empty.
 */
export function useCreateGallery() {
  const [ewUser] = useLocalStorage('ewUser', null)
  const createTopic = useCreateTopic(ewUser?.userID || '')

  const create = useCallback(
    async (title = 'Untitled gallery', sourceAlbumId?: string) => {
      if (!ewUser?.userID) throw new Error('Sign in to create a gallery')
      const result = await createTopic.mutateAsync({
        topicName: title,
        topicType: TopicType.GALLERY.toString(),
        allowContribution: 0,
        // Deliberately not real JSON when there's no album to link — mirrors
        // the placeholder every other lean create in this codebase uses
        // (ModernCreateModal, Header, useAlbumDetail's useCreateAlbum).
        // Every topicXData reader here (parseSourceAlbumId, parseSceneNote)
        // already treats a JSON.parse failure as "no data", same as a
        // freshly created ALBUM.
        topicXData: sourceAlbumId ? withSourceAlbumIds(undefined, [sourceAlbumId]) : 'X Data Here',
        privacyType: PrivacyType.PRIVATE,
        cliques: [],
        connections: [],
      })
      if (result?.isError) throw new Error(result.errorMessage || 'Could not create gallery')
      return result.topicID as string
    },
    [ewUser?.userID, createTopic],
  )

  return { create, isLoading: createTopic.isLoading }
}
